import { useState, type ComponentProps, type ReactElement } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

export interface PasswordInputProps extends Omit<ComponentProps<typeof Input>, 'type'> {
    /** Accessible label when the password is hidden (click to reveal). */
    showLabel?: string;
    /** Accessible label when the password is visible (click to hide). */
    hideLabel?: string;
}

/**
 * Password field with a show/hide visibility toggle. Labels default to English so
 * call sites can omit them; prefer i18n from the surrounding form when available.
 */
export function PasswordInput({
    className,
    showLabel = 'Show password',
    hideLabel = 'Hide password',
    ...props
}: PasswordInputProps): ReactElement {
    const [visible, setVisible] = useState(false);

    return (
        <div className="relative">
            <Input {...props} type={visible ? 'text' : 'password'} className={cn('pr-10', className)} />
            <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center size-5 rounded text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? hideLabel : showLabel}>
                {visible ? (
                    <EyeOff className="size-4 shrink-0" strokeWidth={2.25} />
                ) : (
                    <Eye className="size-4 shrink-0" strokeWidth={2.25} />
                )}
            </button>
        </div>
    );
}
